import { Component } from '@theme/component';
import {
  fetchConfig,
  debounce,
  onAnimationEnd,
  prefersReducedMotion,
  startViewTransition,
} from '@theme/utilities';
import { morphSection, sectionRenderer } from '@theme/section-renderer';
import {
  ThemeEvents,
  CartUpdateEvent,
  QuantitySelectorUpdateEvent,
  CartAddEvent,
  DiscountUpdateEvent,
} from '@theme/events';
import { cartPerformance } from '@theme/performance';

/** @typedef {import('./utilities').TextComponent} TextComponent */

/**
 * A custom element that displays a cart items component.
 *
 * @typedef {object} Refs
 * @property {HTMLElement[]} quantitySelectors - The quantity selector elements.
 * @property {HTMLTableRowElement[]} cartItemRows - The cart item rows.
 * @property {TextComponent} cartTotal - The cart total.
 *
 * @extends {Component<Refs>}
 */
class CartItemsComponent extends Component {
  #debouncedOnChange = debounce(this.#onQuantityChange, 300).bind(this);

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.addEventListener(ThemeEvents.discountUpdate, this.handleDiscountUpdate);
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);
    this.addEventListener('change', this.#onSellingPlanChange);
    this.#syncSellingPlanChoices();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.removeEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);
    this.removeEventListener('change', this.#onSellingPlanChange);
  }

  /**
   * Handles selling plan selector changes.
   * @param {Event} event - The event.
   */
  #onSellingPlanChange = (event) => {
    const target = event.target;

    if (!(target instanceof HTMLInputElement) || target.dataset.sellingPlanSelector !== 'true') return;

    const { line, quantity } = target.dataset;
    if (!line || !quantity) return;

    const optimisticInput = this.#getOptimisticSellingPlanInput(target);

    this.#applyOptimisticSellingPlanChoice(optimisticInput);

    this.updateQuantity({
      line: parseInt(line, 10),
      quantity: parseInt(quantity, 10),
      sellingPlan: optimisticInput.value,
      action: 'selling-plan',
    });
  };

  /**
   * Normalizes subscription-card clicks to the matching frequency option so the visible pill is selected immediately.
   * @param {HTMLInputElement} input - The changed input.
   * @returns {HTMLInputElement} The input to use for the optimistic cart update.
   */
  #getOptimisticSellingPlanInput(input) {
    const panel = input.closest('.ev-cart-plan__panel');
    if (!(panel instanceof HTMLElement) || input.value === '') return input;

    const isSubscriptionCardInput = input.closest('.ev-cart-plan__option--subscription');
    if (!isSubscriptionCardInput) return input;

    const frequencyInput = panel.querySelector(
      `.ev-cart-plan__frequencies input[data-selling-plan-selector="true"][value="${CSS.escape(input.value)}"]`
    );

    if (frequencyInput instanceof HTMLInputElement) {
      frequencyInput.checked = true;
      return frequencyInput;
    }

    return input;
  }

  /**
   * Updates the visible selling-plan state before the cart section is refreshed.
   * @param {HTMLInputElement} input - The selected input.
   * @param {{ updateLineDetails?: boolean }} [options] - Optimistic update options.
   */
  #applyOptimisticSellingPlanChoice(input, options = {}) {
    const panel = input.closest('.ev-cart-plan__panel');
    if (!(panel instanceof HTMLElement)) return;

    panel.dataset.currentSellingPlanId = input.value;

    const subscriptionCard = panel.querySelector('.ev-cart-plan__subscription-card');
    const oneTimeOption = panel.querySelector('.ev-cart-plan__option--one-time');

    if (subscriptionCard instanceof HTMLElement) {
      subscriptionCard.classList.toggle('ev-cart-plan__subscription-card--selected', input.value !== '');
    }

    if (oneTimeOption instanceof HTMLElement) {
      oneTimeOption.classList.toggle('ev-cart-plan__option--one-time-selected', input.value === '');
    }

    for (const frequency of panel.querySelectorAll('.ev-cart-plan__frequency')) {
      const frequencyInput = frequency.querySelector('input[data-selling-plan-selector="true"]');
      const isSelected = frequencyInput instanceof HTMLInputElement && frequencyInput.value === input.value && input.value !== '';
      frequency.classList.toggle('ev-cart-plan__frequency--selected', isSelected);
    }

    if (options.updateLineDetails !== false) {
      this.#updateOptimisticLineDetails(panel, input);
    }
  }

  /**
   * Optimistically updates the line price and cadence next to the cart item while Shopify refreshes the section.
   * @param {HTMLElement} panel - The selling-plan panel.
   * @param {HTMLInputElement} input - The selected input.
   */
  #updateOptimisticLineDetails(panel, input) {
    const subscriptionRow = panel.closest('.cart-items__subscription-row');
    const itemRow = subscriptionRow?.previousElementSibling;
    if (!(itemRow instanceof HTMLElement)) return;

    const optimisticPrice = input.dataset.linePrice;
    const priceText = itemRow.querySelector('.cart-items__price text-component');
    if (optimisticPrice && priceText instanceof HTMLElement) {
      priceText.setAttribute('value', optimisticPrice);
      priceText.textContent = optimisticPrice;
    }

    const variantsWrapper = itemRow.querySelector('.cart-items__variants-wrapper');
    if (!(variantsWrapper instanceof HTMLElement)) return;

    const existingCadence = variantsWrapper.querySelector('.ev-cart__cadence');
    const planName = input.dataset.sellingPlanName || '';

    if (!input.value) {
      existingCadence?.remove();
      return;
    }

    if (existingCadence instanceof HTMLElement) {
      existingCadence.textContent = planName;
      return;
    }

    const cadence = document.createElement('p');
    cadence.className = 'ev-cart__cadence';
    cadence.textContent = planName;
    variantsWrapper.append(cadence);
  }

  /**
   * Syncs selling plan UI choices after initial render and section morphing.
   */
  #syncSellingPlanChoices() {
    const panels = this.querySelectorAll('.ev-cart-plan__panel');

    for (const panel of panels) {
      if (!(panel instanceof HTMLElement)) continue;

      const currentSellingPlanId = panel.dataset.currentSellingPlanId ?? '';
      const input = panel.querySelector(
        currentSellingPlanId
          ? `input[data-selling-plan-selector="true"][value="${CSS.escape(currentSellingPlanId)}"]`
          : 'input[data-selling-plan-selector="true"][value=""]'
      );

      if (!(input instanceof HTMLInputElement)) continue;

      input.checked = true;
      this.#applyOptimisticSellingPlanChoice(input, { updateLineDetails: false });
    }
  }

  /**
   * Handles QuantitySelectorUpdateEvent change event.
   * @param {QuantitySelectorUpdateEvent} event - The event.
   */
  #onQuantityChange(event) {
    if (!(event.target instanceof Node) || !this.contains(event.target)) return;

    const { quantity, cartLine: line } = event.detail;

    // Cart items require a line number
    if (!line) return;

    if (quantity === 0) {
      return this.onLineItemRemove(line);
    }

    this.updateQuantity({
      line,
      quantity,
      action: 'change',
    });
    const lineItemRow = this.refs.cartItemRows[line - 1];

    if (!lineItemRow) return;
  }

  /**
   * Handles the line item removal.
   * @param {number} line - The line item index.
   */
  onLineItemRemove(line) {
    this.updateQuantity({
      line,
      quantity: 0,
      action: 'clear',
    });

    const cartItemRowToRemove = this.refs.cartItemRows[line - 1];

    if (!cartItemRowToRemove) return;

    const rowsToRemove = [
      cartItemRowToRemove,
      // Get all nested lines of the row to remove
      ...this.refs.cartItemRows.filter((row) => row.dataset.parentKey === cartItemRowToRemove.dataset.key),
    ];

    // If the cart item row is the last row, optimistically trigger the cart empty state
    const isEmptyCart = rowsToRemove.length == this.refs.cartItemRows.length;

    const template = document.getElementById('empty-cart-template');
    if (isEmptyCart && template instanceof HTMLTemplateElement) {
      const clone = document.importNode(template.content, true);
      const drawerDialog = this.closest('.cart-drawer__dialog');

      startViewTransition(() => {
        if (drawerDialog instanceof HTMLElement) {
          drawerDialog.classList.add('cart-drawer--empty');
        }
        this.replaceChildren(clone);
      }, [this.isDrawer ? 'empty-cart-drawer' : 'empty-cart-page']);

      return;
    }

    // Add class to the row to trigger the animation
    rowsToRemove.forEach((row) => {
      const remove = () => row.remove();

      if (prefersReducedMotion()) return remove();

      row.style.setProperty('--row-height', `${row.clientHeight}px`);
      row.classList.add('removing');

      // Remove the row after the animation ends
      onAnimationEnd(row, remove);
    });
  }

  /**
   * Updates the quantity.
   * @param {Object} config - The config.
   * @param {number} config.line - The line.
   * @param {number} config.quantity - The quantity.
   * @param {string} config.action - The action.
   * @param {string} [config.sellingPlan] - The selling plan ID.
   */
  updateQuantity(config) {
    const cartPerformaceUpdateMarker = cartPerformance.createStartingMarker(`${config.action}:user-action`);

    this.#disableCartItems();

    const { line, quantity } = config;
    const { cartTotal } = this.refs;

    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    const sectionsToUpdate = new Set([this.sectionId]);
    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        sectionsToUpdate.add(item.dataset.sectionId);
      }
    });

    const bodyData = {
      line: line,
      quantity: quantity,
      sections: Array.from(sectionsToUpdate).join(','),
      sections_url: window.location.pathname,
    };

    if (config.sellingPlan !== undefined) {
      bodyData.selling_plan = config.sellingPlan;
    }

    const body = JSON.stringify(bodyData);

    fetch(`${Theme.routes.cart_change_url}`, fetchConfig('json', { body }))
      .then((response) => {
        return response.text();
      })
      .then((responseText) => {
        const parsedResponseText = JSON.parse(responseText);

        if (parsedResponseText.errors) {
          this.#handleCartError(line, parsedResponseText);
          return;
        }

        const newSectionHTML = new DOMParser().parseFromString(
          parsedResponseText.sections[this.sectionId],
          'text/html'
        );

        // Grab the new cart item count from a hidden element
        const newCartHiddenItemCount = newSectionHTML.querySelector('[ref="cartItemCount"]')?.textContent;
        const newCartItemCount = newCartHiddenItemCount ? parseInt(newCartHiddenItemCount, 10) : 0;

        // Update data-cart-quantity for all matching variants
        this.#updateQuantitySelectors(parsedResponseText);

        this.dispatchEvent(
          new CartUpdateEvent(parsedResponseText, this.sectionId, {
            itemCount: newCartItemCount,
            source: 'cart-items-component',
            sections: parsedResponseText.sections,
          })
        );

        morphSection(this.sectionId, parsedResponseText.sections[this.sectionId], this.isDrawer ? 'hydration' : 'full');

        this.#updateCartQuantitySelectorButtonStates();
        this.#syncSellingPlanChoices();
      })
      .catch((error) => {
        console.error(error);
      })
      .finally(() => {
        this.#enableCartItems();
        cartPerformance.measureFromMarker(cartPerformaceUpdateMarker);
      });
  }

  /**
   * Handles the discount update.
   * @param {DiscountUpdateEvent} event - The event.
   */
  handleDiscountUpdate = (event) => {
    this.#handleCartUpdate(event);
  };

  /**
   * Handles the cart error.
   * @param {number} line - The line.
   * @param {Object} parsedResponseText - The parsed response text.
   * @param {string} parsedResponseText.errors - The errors.
   */
  #handleCartError = (line, parsedResponseText) => {
    const quantitySelector = this.refs.quantitySelectors[line - 1];
    const quantityInput = quantitySelector?.querySelector('input');

    if (!quantityInput) throw new Error('Quantity input not found');

    quantityInput.value = quantityInput.defaultValue;

    const cartItemError = this.refs[`cartItemError-${line}`];
    const cartItemErrorContainer = this.refs[`cartItemErrorContainer-${line}`];

    if (!(cartItemError instanceof HTMLElement)) throw new Error('Cart item error not found');
    if (!(cartItemErrorContainer instanceof HTMLElement)) throw new Error('Cart item error container not found');

    cartItemError.textContent = parsedResponseText.errors;
    cartItemErrorContainer.classList.remove('hidden');
  };

  /**
   * Handles the cart update.
   *
   * @param {DiscountUpdateEvent | CartUpdateEvent | CartAddEvent} event
   */
  #handleCartUpdate = (event) => {
    if (event instanceof DiscountUpdateEvent) {
      sectionRenderer.renderSection(this.sectionId, { cache: false });
      return;
    }
    if (event.target === this) return;

    const cartItemsHtml = event.detail.data.sections?.[this.sectionId];
    if (cartItemsHtml) {
      morphSection(this.sectionId, cartItemsHtml);

      // Update button states for all cart quantity selectors after morph
      this.#updateCartQuantitySelectorButtonStates();
      this.#syncSellingPlanChoices();
    } else {
      sectionRenderer.renderSection(this.sectionId, { cache: false });
    }
  };

  /**
   * Disables the cart items.
   */
  #disableCartItems() {
    this.classList.add('cart-items-disabled');
  }

  /**
   * Enables the cart items.
   */
  #enableCartItems() {
    this.classList.remove('cart-items-disabled');
  }

  /**
   * Updates quantity selectors for all matching variants in the cart.
   * @param {Object} updatedCart - The updated cart object.
   * @param {Array<{variant_id: number, quantity: number}>} [updatedCart.items] - The cart items.
   */
  #updateQuantitySelectors(updatedCart) {
    if (!updatedCart.items) return;

    for (const item of updatedCart.items) {
      const variantId = item.variant_id.toString();
      const selectors = document.querySelectorAll(`quantity-selector-component[data-variant-id="${variantId}"]`);

      for (const selector of selectors) {
        const input = selector.querySelector('input[data-cart-quantity]');
        if (!input) continue;

        input.setAttribute('data-cart-quantity', item.quantity.toString());

        // Update the quantity selector's internal state
        if ('updateCartQuantity' in selector && typeof selector.updateCartQuantity === 'function') {
          selector.updateCartQuantity();
        }
      }
    }
  }

  /**
   * Updates button states for all cart quantity selector components.
   */
  #updateCartQuantitySelectorButtonStates() {
    for (const selector of document.querySelectorAll('cart-quantity-selector-component')) {
      /** @type {any} */ (selector).updateButtonStates?.();
    }
  }

  /**
   * Gets the section id.
   * @returns {string} The section id.
   */
  get sectionId() {
    const { sectionId } = this.dataset;

    if (!sectionId) throw new Error('Section id missing');

    return sectionId;
  }

  /**
   * @returns {boolean} Whether the component is a drawer.
   */
  get isDrawer() {
    return this.dataset.drawer !== undefined;
  }
}

if (!customElements.get('cart-items-component')) {
  customElements.define('cart-items-component', CartItemsComponent);
}
